import {
  type CSSProperties,
  createContext,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type ReactNode,
  type Ref,
  type TdHTMLAttributes,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PanePinned } from '../../app/shell/PaneHost';
import { cx } from '../cx';
import { Icon } from '../Icon/Icon';
import { PanEdges, usePanEdges } from '../PanEdges/PanEdges';
import { ScrollEdge } from '../ScrollEdge/ScrollEdge';

/**
 * THE TABLE (BR-PARTS; DESIGN.md B1, B19, C7, C8, C10, C15, F2, F3, F10; register A6-7).
 *
 * One part for every table a person reads on the redesigned pages: the Clients list, the
 * Onboarding checklist and the report's numbers drew three tables of their own, and each aged
 * differently (the Clients table had a minimum width, the Onboarding table none, so Onboarding
 * broke at a 950 px window in Safari, A6-7). What the part owns, and every caller gets:
 *
 * COLUMN WIDTHS FROM CONTENT (C7, C8, N20). The body table lays out automatically, every head
 * and every cell on one line unless its column wraps, so a column is as wide as its longest
 * heading or cell plus its padding - never a pixel copied from a mockup and never below the
 * 40 px target (`--touch-target-min`). A column marked `fill` takes what the frame leaves over
 * when it is wider than the table; without one the browser shares it out by content.
 *
 * IT PANS INSIDE ITS OWN FRAME (C10). When the window is narrower than the columns, the table
 * scrolls sideways in its frame while the page, its title and its filters hold still; the
 * first column (the row's name) stays pinned, and a crisp shading strip shows at an edge only
 * while that side still holds content (`ui/PanEdges`, no blur over data). A frame that holds
 * more than it shows is a tab stop, so the columns past its edge are reachable by keyboard.
 *
 * THE HEAD STAYS AT THE TOP (F3, C15). The head a person sees is pinned under the window
 * toolbar by the pane host (`PanePinned`) while the rows pass under it, with the hard scroll
 * edge that pinned table headers take (WWDC 2025/356 at 11:18; kit `Scroll Edge Effect/Light -
 * Top - Hard`). It has to stand OUTSIDE the sideways scroller, because a scroller is the sticky
 * box of everything inside it and would trap the head (ledger FE-CL-13: WebKit gave an edge
 * inside the table's own head the whole table as its containing block). So the visible head is
 * a table of its own, panned by the body's `scrollLeft`, whose columns take the body's measured
 * widths; the body keeps a head of its own, one pixel tall, which is the head a screen reader
 * reads (`scope`, `aria-sort`) and the head whose content sizes the columns.
 *
 * NO WRAPPING IN A HEAD OR A SYMBOL CELL (C7, Part 3 item 2, measure R8). Every `th` keeps its
 * text on one line, a cell of a `symbol` column too; a column that may wrap says so (`wrap`).
 *
 * SORT HEADS AND GROUP ROWS REACH THE 40 PX FLOOR (B19) through `.hit-expand`, which grows the
 * pressable box without moving a pixel.
 *
 * WHAT STAYS WITH THE CALLER: the rows and what is in them (`TableSection`, `TableRow`,
 * `TableCell`, `TableGroupRow`, `TableSpanRow`), the cells' padding, which is the page's density
 * (C4), and the words. The raw table elements live here and nowhere else (B1, `lint_ui`).
 */

export type TableAlign = 'start' | 'center' | 'end';

export interface TableSort {
  /** The column's current order, or null when the list is sorted by another column. */
  readonly direction: 'ascending' | 'descending' | null;
  readonly onSort: () => void;
}

/** A `data-*` attribute map, for the tests' and the measure's hooks. */
export type DataAttributes = Readonly<Record<`data-${string}`, string>>;

export interface TableColumn {
  /** The column's identity; a cell names its column by it. */
  readonly key: string;
  /**
   * What the head prints. Plain content: it is drawn twice, in the head a person sees and in
   * the body's own head that a screen reader reads and that sizes the column. Null for a
   * column with no heading (a trailing chevron).
   */
  readonly header: ReactNode;
  readonly align?: TableAlign | undefined;
  /** A column of symbols (step marks): its cells never wrap, the measure's step cells (R8). */
  readonly symbol?: boolean | undefined;
  /** The column's cells may wrap at their words; every other column keeps a cell on one line. */
  readonly wrap?: boolean | undefined;
  /** A floor of the column's own above the 40 px target, a CSS length (a wrapped column's measure). */
  readonly minWidth?: string | undefined;
  /** Takes the width the frame leaves over. */
  readonly fill?: boolean | undefined;
  /** Makes the heading a sort button (F10): first click ascending, again to reverse. */
  readonly sort?: TableSort | undefined;
  /**
   * The head cell's padding and any weight of its own, in place of the part's `px-1.5 py-2`;
   * the same classes size the column in the body's head, so the two heads keep one width.
   */
  readonly headClassName?: string | undefined;
  /** Attributes on the head cell a person sees (`data-step-head`). */
  readonly headAttributes?: DataAttributes | undefined;
  /** Attributes on the sort button (`data-sort-key`). */
  readonly sortAttributes?: DataAttributes | undefined;
}

export interface TableProps {
  /** The table's accessible name ("Clients"), and the frame's when it pans. */
  readonly label: string;
  readonly columns: readonly TableColumn[];
  /** The body: `TableSection`s, or rows straight away. */
  readonly children: ReactNode;
  /**
   * `edge` (default): the head's ground is the hard scroll edge, which pinned table headers
   * take (C15). `solid`: the head cells are filled with the canvas.
   */
  readonly headGround?: 'edge' | 'solid' | undefined;
  /** Attributes on the head a person sees (`data-clients-head`). */
  readonly headAttributes?: DataAttributes | undefined;
  /** Attributes on the body table (`data-onboarding-matrix`). */
  readonly tableAttributes?: DataAttributes | undefined;
  readonly tableRef?: Ref<HTMLTableElement> | undefined;
  readonly onKeyDown?: KeyboardEventHandler<HTMLTableElement> | undefined;
  /** Classes on the frame (spacing around the table is the page's). */
  readonly className?: string | undefined;
}

interface TableContextValue {
  readonly columns: readonly TableColumn[];
  readonly index: ReadonlyMap<string, number>;
}

const TableContext = createContext<TableContextValue | null>(null);

function useTable(): TableContextValue {
  const value = useContext(TableContext);
  if (value === null) throw new Error('A table row or cell renders inside a Table.');
  return value;
}

const ALIGN: Readonly<Record<TableAlign, string>> = {
  start: 'text-left',
  center: 'text-center',
  end: 'text-right',
};

/** The head's type: the list head's footnote in the secondary ink (`.list-head th`). */
const HEAD_TEXT = 'text-2xs tracking-normal';
/** A head cell's padding unless its column gives its own (`headClassName`). */
const HEAD_INSET = 'px-1.5 py-2';

/**
 * The first column stays put while the others pan under it. Its ground is the canvas so the
 * cells passing under it do not show through, and it takes the row's hover and keyboard fill
 * when the row has them (`TableRow hover`).
 */
const PINNED =
  'sticky left-0 z-[1] bg-canvas transition-colors duration-(--duration-fast) group-hover/row:bg-surface-hover group-has-[:focus-visible]/focus:bg-surface-hover';

/** Hands the body table to the caller's ref as well as to the part's own. */
function assignRef(ref: Ref<HTMLTableElement> | undefined, node: HTMLTableElement | null) {
  if (typeof ref === 'function') ref(node);
  else if (ref !== null && ref !== undefined) {
    (ref as { current: HTMLTableElement | null }).current = node;
  }
}

/** Two width lists that would draw the same head. */
function sameWidths(a: readonly number[], b: readonly number[]): boolean {
  return (
    a.length === b.length && a.every((width, index) => Math.abs(width - (b[index] ?? 0)) < 0.5)
  );
}

interface Geometry {
  /** The body's measured column widths, in px, in column order. */
  readonly widths: readonly number[];
  /** The frame's visible width, for the rows whose words must stay in view while it pans. */
  readonly visible: number;
}

/**
 * The chevron the sorted column shows (R-K1, F10), and its slot in the sizing head, which every
 * sortable column reserves so a sort never changes a width.
 */
function SortMark({ direction }: { readonly direction: TableSort['direction'] }) {
  return (
    <span
      className={cx('inline-flex text-accent', direction === null && 'invisible')}
      aria-hidden="true"
    >
      <Icon name={direction === 'descending' ? 'chevron-down' : 'chevron-up'} size="0.75rem" />
    </span>
  );
}

/** One heading as a person sees it: its words, or a sort button with its chevron. */
function HeadContent({ column }: { readonly column: TableColumn }) {
  const sort = column.sort;
  if (sort === undefined) return <>{column.header}</>;
  return (
    <button
      type="button"
      onClick={sort.onSort}
      className="hit-expand inline-flex cursor-default items-center gap-1 hover:text-ink"
      {...column.sortAttributes}
    >
      {column.header}
      {sort.direction !== null && <SortMark direction={sort.direction} />}
    </button>
  );
}

export function Table({
  label,
  columns,
  children,
  headGround = 'edge',
  headAttributes,
  tableAttributes,
  tableRef,
  onKeyDown,
  className,
}: TableProps) {
  const body = useRef<HTMLTableElement | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const headViewport = useRef<HTMLDivElement>(null);
  const edges = usePanEdges(scroller);
  const [geometry, setGeometry] = useState<Geometry | null>(null);
  const context = useMemo<TableContextValue>(
    () => ({ columns, index: new Map(columns.map((column, at) => [column.key, at])) }),
    [columns],
  );
  const columnKeys = columns.map((column) => column.key).join('\u0000');
  const fills = columns.some((column) => column.fill === true);

  /* THE BODY SIZES THE COLUMNS AND THE HEAD TAKES ITS WIDTHS, read after layout and again
     whenever a column or the frame changes size; an unchanged reading sets no state. */
  useLayoutEffect(() => {
    const table = body.current;
    const frame = scroller.current;
    if (table === null || frame === null || columnKeys === '') return;
    /* Read by selector rather than `tHead.rows`, which the unit tests' document lacks. */
    const sizingCells = () => Array.from(table.querySelectorAll(':scope > thead > tr > th'));
    const measure = () => {
      const cells = sizingCells();
      if (cells.length === 0) return;
      const widths = cells.map((cell) => cell.getBoundingClientRect().width);
      const visible = frame.clientWidth;
      setGeometry((current) =>
        current !== null && current.visible === visible && sameWidths(current.widths, widths)
          ? current
          : { widths, visible },
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    observer.observe(table);
    for (const cell of sizingCells()) observer.observe(cell);
    return () => observer.disconnect();
  }, [columnKeys]);

  const pinnedWidth = geometry?.widths[0] ?? 0;
  const pans = edges.start || edges.end;
  const frameStyle = {
    '--table-visible': geometry === null ? undefined : `${String(geometry.visible)}px`,
    scrollPaddingInlineStart: pinnedWidth === 0 ? undefined : `${String(pinnedWidth)}px`,
  } as CSSProperties;

  return (
    <TableContext.Provider value={context}>
      <div className={cx('flex min-w-0 flex-col', className)} data-table-frame="">
        <PanePinned>
          <div className="relative" {...headAttributes}>
            {headGround === 'edge' && <ScrollEdge host="column-head" style="hard" />}
            {/* THE HEAD'S VIEWPORT, the one box here that is meant to clip: it shows the head
                table at the body's own `scrollLeft`, so whatever is panned out of the body is
                panned out of the head too. */}
            <div
              ref={headViewport}
              className="overflow-hidden"
              data-measure-ignore="clipped-text:the column head is a viewport onto the body's own table width and pans with it, so content past its edge is the horizontal pan working"
            >
              <table
                role="presentation"
                className={cx(
                  'cursor-default border-separate border-spacing-0 text-sm',
                  geometry === null ? 'w-full' : 'table-fixed',
                )}
                style={
                  geometry === null
                    ? undefined
                    : {
                        width: `${String(geometry.widths.reduce((sum, width) => sum + width, 0))}px`,
                      }
                }
              >
                {geometry !== null && (
                  <colgroup>
                    {columns.map((column, at) => (
                      <col
                        key={column.key}
                        style={{ width: `${String(geometry.widths[at] ?? 0)}px` }}
                      />
                    ))}
                  </colgroup>
                )}
                <thead className="list-head">
                  <tr>
                    {columns.map((column, at) => (
                      <th
                        key={column.key}
                        className={cx(
                          column.headClassName ?? HEAD_INSET,
                          ALIGN[column.align ?? 'start'],
                          (headGround === 'solid' || at === 0) && 'bg-canvas',
                          at === 0 && 'sticky left-0 z-[1]',
                        )}
                        {...column.headAttributes}
                      >
                        <HeadContent column={column} />
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>
            <PanEdges start={edges.start} end={edges.end} startInset={pinnedWidth} />
          </div>
        </PanePinned>
        {/* THE STRIPS ARE THE SCROLLER'S SIBLINGS, so they stay at the frame's edges instead of
            panning away with the content they guard; `min-w-0` lets the frame be narrower than
            its table, which is what makes it pan rather than push the page sideways. */}
        <div className="relative min-w-0">
          {/* A FRAME THAT PANS IS A TAB STOP, named like its table, so the columns past its edge
              are reachable without a pointer; one that shows everything is not. */}
          <div
            ref={scroller}
            onScroll={(event) => {
              if (headViewport.current !== null) {
                headViewport.current.scrollLeft = event.currentTarget.scrollLeft;
              }
            }}
            {...(pans ? { tabIndex: 0, role: 'region', 'aria-label': label } : {})}
            data-table-pan=""
            style={frameStyle}
            className="relative min-w-0 overflow-x-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
          >
            <table
              ref={(node) => {
                body.current = node;
                assignRef(tableRef, node);
              }}
              aria-label={label}
              onKeyDown={onKeyDown}
              className="w-full cursor-default border-separate border-spacing-0 text-sm"
              {...tableAttributes}
            >
              {/* THE HEAD A SCREEN READER READS, AND THE ONE THAT SIZES THE COLUMNS: the same
                  words as the head above at the same type and padding, on one line, each at
                  least the 40 px target, a sortable one with its chevron's slot reserved, in a
                  row one pixel tall that nobody sees. A `fill` table gives every other column
                  `width: 1px`, which automatic layout reads as "as narrow as the content". */}
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      {...(column.sort?.direction === null || column.sort === undefined
                        ? {}
                        : { 'aria-sort': column.sort.direction })}
                      className="h-px border-0 p-0 font-normal"
                      style={fills && column.fill !== true ? { width: '1px' } : undefined}
                    >
                      <div className="h-px overflow-hidden">
                        <div
                          className={cx(
                            column.headClassName ?? HEAD_INSET,
                            HEAD_TEXT,
                            'inline-flex min-w-(--touch-target-min) items-center gap-1 whitespace-nowrap',
                          )}
                          style={
                            column.minWidth === undefined
                              ? undefined
                              : { minWidth: column.minWidth }
                          }
                        >
                          {column.header}
                          {column.sort !== undefined && <SortMark direction={null} />}
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              {children}
            </table>
          </div>
          <PanEdges start={edges.start} end={edges.end} startInset={pinnedWidth} />
        </div>
      </div>
    </TableContext.Provider>
  );
}

/** A group of rows (`<tbody>`), with the caller's attributes and keyboard handler. */
export function TableSection(props: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props} />;
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** The row opens or ticks something: the hover fill and the arrow pointer (F11). */
  readonly hover?: boolean | undefined;
  /**
   * The row's keyboard focus looks like its hover (components page section 3): the fill while a
   * control in it has keyboard focus, for a row whose focus is the row's, not a cell's.
   */
  readonly focusFill?: boolean | undefined;
}

/** A row; the pinned cell takes the row's hover and focus fill with it. */
export function TableRow({ hover = false, focusFill = false, className, ...rest }: TableRowProps) {
  return (
    <tr
      className={cx(
        hover &&
          'group/row cursor-default transition-colors duration-(--duration-fast) hover:bg-surface-hover',
        focusFill && 'group/focus has-[:focus-visible]:bg-surface-hover',
        className,
      )}
      {...rest}
    />
  );
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** The column's key; the cell takes its alignment, wrapping and pinning from it. */
  readonly column: string;
  /** The row's name: a `<th scope="row">` (the first column, as a rule). */
  readonly rowHeader?: boolean | undefined;
}

/**
 * One cell. The part gives it the row's hairline, the column's alignment, one line unless the
 * column wraps, and the pinned ground in the first column; the caller gives its padding.
 */
export function TableCell({ column, rowHeader = false, className, ...rest }: TableCellProps) {
  const { columns, index } = useTable();
  const at = index.get(column);
  const definition = at === undefined ? undefined : columns[at];
  const classes = cx(
    'border-b border-line-subtle align-middle',
    ALIGN[definition?.align ?? 'start'],
    definition?.wrap === true ? undefined : 'whitespace-nowrap',
    at === 0 && PINNED,
    rowHeader && 'font-normal',
    className,
  );
  const marker = definition?.symbol === true ? { 'data-symbol-cell': '' } : {};
  return rowHeader ? (
    <th scope="row" className={classes} {...marker} {...rest} />
  ) : (
    <td className={classes} {...marker} {...rest} />
  );
}

export interface TableGroupRowProps extends HTMLAttributes<HTMLTableRowElement> {
  readonly expanded: boolean;
  readonly onToggle: () => void;
  /** What the group row says after its chevron: its mark, name, hint, count, composition. */
  readonly children: ReactNode;
  /** Attributes on the disclosure button (`data-list-nav`). */
  readonly buttonAttributes?: DataAttributes | undefined;
}

/**
 * A GROUP ROW IS ONE DISCLOSURE BUTTON (F9; components page section 3): the chevron and the
 * caller's words, across every column, staying in view while the table pans, and pressed at the
 * 40 px floor without growing the row (B19).
 */
export function TableGroupRow({
  expanded,
  onToggle,
  children,
  buttonAttributes,
  className,
  ...rest
}: TableGroupRowProps) {
  const { columns } = useTable();
  return (
    <tr
      className={cx('group/row has-[button:focus-visible]:bg-surface-hover', className)}
      {...rest}
    >
      <th
        scope="rowgroup"
        colSpan={columns.length}
        className="border-b border-line-strong p-0 text-left font-normal"
      >
        <div className="sticky left-0 w-[var(--table-visible,100%)]">
          <button
            type="button"
            aria-expanded={expanded}
            onClick={onToggle}
            className="hit-expand flex min-h-6 w-full cursor-default items-center gap-2 px-1.5 pt-2.5 pb-2 text-left font-semibold whitespace-nowrap text-ink focus-visible:outline-none"
            {...buttonAttributes}
          >
            <span
              className="grid size-4 shrink-0 place-items-center text-ink-muted"
              aria-hidden="true"
            >
              <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size="0.75rem" />
            </span>
            {children}
          </button>
        </div>
      </th>
    </tr>
  );
}

export interface TableSpanRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** The cell's own classes: a spacer's height, a border. */
  readonly cellClassName?: string | undefined;
  /**
   * The content's classes, its padding above all. The padding sits INSIDE the box that is as
   * wide as the frame shows, never on the cell: a cell padded around a frame-wide box is wider
   * than the frame, and the table would pan by that padding at every width.
   */
  readonly contentClassName?: string | undefined;
  readonly children?: ReactNode;
}

/**
 * A row across every column: a group's footer ("Show 12 more"), a spacer between groups. Its
 * content stays in view while the table pans, the way the group row's does.
 */
export function TableSpanRow({
  cellClassName,
  contentClassName,
  children,
  ...rest
}: TableSpanRowProps) {
  const { columns } = useTable();
  return (
    <tr {...rest}>
      <td colSpan={columns.length} className={cx('p-0', cellClassName)}>
        {children === undefined ? null : (
          <div className={cx('sticky left-0 w-[var(--table-visible,100%)]', contentClassName)}>
            {children}
          </div>
        )}
      </td>
    </tr>
  );
}
